import { Form, Head } from "@inertiajs/react"
import { useState } from "react"

import { FormField } from "@/components/form-field"
import { InputError } from "@/components/input-error"
import { TextLink } from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { signInPath } from "@/routes"

export default function Register({ passphrase }: { passphrase: string }) {
  const [emailNotifications, setEmailNotifications] = useState(true)

  return (
    <AuthLayout
      title="Create an account"
      description="Enter your details below to create your account"
    >
      <Head title="Register" />
      <Form
        method="post"
        action="/sign_up"
        resetOnSuccess={["password", "password_confirmation"]}
        disableWhileProcessing
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <>
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="passphrase">Secret Passphrase</Label>
                <Input
                  id="passphrase"
                  type="text"
                  name="passphrase"
                  required
                  autoFocus
                  defaultValue={passphrase}
                  disabled={processing}
                  placeholder="e.g. goblin-blitz-touchdown"
                  aria-invalid={!!errors.passphrase || undefined}
                  aria-describedby={
                    errors.passphrase ? "passphrase-error" : "passphrase-hint"
                  }
                />
                <InputError
                  id="passphrase-error"
                  messages={errors.passphrase}
                />
                {!errors.passphrase && (
                  <p
                    id="passphrase-hint"
                    className="text-muted-foreground text-xs"
                  >
                    Get this from your league commissioner&apos;s invite link.
                  </p>
                )}
              </div>

              <FormField
                label="Name"
                htmlFor="name"
                error={errors.name?.join(", ")}
              >
                <Input
                  id="name"
                  type="text"
                  name="name"
                  required
                  autoComplete="name"
                  disabled={processing}
                  placeholder="Full name"
                />
              </FormField>

              <FormField
                label="Email address"
                htmlFor="email"
                error={errors.email?.join(", ")}
              >
                <Input
                  id="email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="email@example.com"
                />
              </FormField>

              <FormField
                label="Password"
                htmlFor="password"
                error={errors.password?.join(", ")}
              >
                <Input
                  id="password"
                  type="password"
                  name="password"
                  required
                  autoComplete="new-password"
                  placeholder="Password"
                />
              </FormField>

              <FormField
                label="Confirm password"
                htmlFor="password_confirmation"
                error={errors.password_confirmation?.join(", ")}
              >
                <Input
                  id="password_confirmation"
                  type="password"
                  name="password_confirmation"
                  required
                  autoComplete="new-password"
                  placeholder="Confirm password"
                />
              </FormField>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="email_notifications"
                  checked={emailNotifications}
                  onCheckedChange={(checked) =>
                    setEmailNotifications(checked === true)
                  }
                  disabled={processing}
                />
                <Input
                  type="hidden"
                  name="email_notifications"
                  value={emailNotifications ? "1" : "0"}
                />
                <Label
                  htmlFor="email_notifications"
                  className="text-sm font-normal"
                >
                  Email me the round recap — reply-all to talk trash
                </Label>
              </div>

              <Button type="submit" className="mt-2 w-full">
                {processing && <Spinner />}
                Create account
              </Button>
            </div>

            <div className="text-muted-foreground text-center text-sm">
              Already have an account?{" "}
              <TextLink href={signInPath()}>Log in</TextLink>
            </div>
          </>
        )}
      </Form>
    </AuthLayout>
  )
}
